import { defineMessages, useIntl } from 'react-intl';
import { useRouteMatch } from 'react-router-dom';
import { INITIAL_KSS_TEXT_VALUES } from './constants';

const i18nMessages = defineMessages({
  addNewLibrary: {
    id: 'menu.item-bank-management.add-new-library',
    defaultMessage: 'Add New Library',
  },
  addNewCompetency: {
    id: 'menu.item-bank-management.add-new-competency',
    defaultMessage: 'Add New Competency',
  },
  addNewAbility: {
    id: 'menu.item-bank-management.add-new-ability',
    defaultMessage: 'Add New Ability',
  },
  addNewSurveyItems: {
    id: 'menu.item-bank-management.add-new-survey-items',
    defaultMessage: 'Add New Survey Items',
  },
  addNewKssTexts: {
    id: 'menu.item-bank-management.add-new-kss-texts',
    defaultMessage: 'Add New KSS Texts',
  },
});

const CommonService = () => {
  const { formatMessage } = useIntl();

  const addLibraryMatch = useRouteMatch({
    path: '/accounts/:aid/add-itembank-survey-content/new',
    exact: true,
    strict: true,
  });

  const addCompetencyMatch = useRouteMatch({
    path: '/accounts/:aid/add-itembank-survey-content/new/add-competency',
    exact: true,
    strict: true,
  });

  const addAbilityMatch = useRouteMatch({
    path: '/accounts/:aid/add-itembank-survey-content/new/add-ability',
    exact: true,
    strict: true,
  });

  const addSurveyItemsMatch = useRouteMatch({
    path: '/accounts/:aid/add-itembank-survey-content/new/add-survey-items',
    exact: true,
    strict: true,
  });

  const addKssTextsMatch = useRouteMatch({
    path: '/accounts/:aid/add-itembank-survey-content/new/add-kss-texts',
    exact: true,
    strict: true,
  });

  const addAbilityToExistingCompetencyMatch = useRouteMatch({
    path: '/accounts/:aid/add-itembank-survey-content/update',
    exact: true,
    strict: true,
  });

  const addSurveyItemsToExistingCompetencyMatch = useRouteMatch({
    path: '/accounts/:aid/add-itembank-survey-content/update/add-survey-items',
    exact: true,
    strict: true,
  });

  const addKssTextsToExistingCompetencyMatch = useRouteMatch({
    path: '/accounts/:aid/add-itembank-survey-content/update/add-kss-texts',
    exact: true,
    strict: true,
  });

  const getLibraryInput = (values) => {
    const { library } = values;

    const libraryInputExists = library.code && library.displayName && library.description;

    if (libraryInputExists) {
      return {
        code: library.code,
        displayName: library.displayName,
        description: library.description,
        isPrivate: library.isPrivate,
      };
    }

    return null;
  };

  const getCompetencyInput = values => ({
    code: values.competency.code,
    displayName: values.competency.displayName,
    description: values.competency.description,
    libraryCode: values.competency.libraryCode,
  });

  const getAbilityInput = values => ({
    code: values.ability.code,
    displayName: values.ability.displayName,
    description: values.ability.description,
  });

  const getAbilityForExistingCompetencyInput = values => ({
    code: values.ability.code,
    displayName: values.ability.displayName,
    description: values.ability.description,
    competencyId: values.ability.competencyId,
  });

  const getItemsInput = values => values.surveyItems.map(surveyItem => ({
    code: surveyItem.code,
    text: surveyItem.text,
    selfText: surveyItem.selfText,
  }));

  const getKssTextsInput = values => values.kssTexts.map(kssText => ({
    itemCode: kssText.itemCode,
    kssTextType: kssText.kssTextType,
    textLevel: kssText.level,
    sequence: kssText.sequence,
    text: kssText.text,
  }));

  const getHeader = () => {
    if (addLibraryMatch) return formatMessage(i18nMessages.addNewLibrary);

    if (addCompetencyMatch) return formatMessage(i18nMessages.addNewCompetency);

    if (addAbilityMatch || addAbilityToExistingCompetencyMatch) return formatMessage(i18nMessages.addNewAbility);

    if (addSurveyItemsMatch
        || addSurveyItemsToExistingCompetencyMatch) return formatMessage(i18nMessages.addNewSurveyItems);

    if (addKssTextsMatch
        || addKssTextsToExistingCompetencyMatch) return formatMessage(i18nMessages.addNewKssTexts);

    return '';
  };

  const getNextKssTextValues = (
    surveyItemValues,
    values,
    availableKssTextTypes,
    availableTextLevels,
    availableSequences,
  ) => {
    const {
      itemCode, kssTextType, level, sequence,
    } = values;

    const surveyItemCodes = surveyItemValues.map(surveyItem => surveyItem.code);

    const filteredTextLevels = availableTextLevels
      .find(textLevel => textLevel.kssTextType === kssTextType).levels;

    const filteredSequences = availableSequences.find(
      kssTextSequence => kssTextSequence.kssTextType === kssTextType && kssTextSequence.level === level,
    ).sequences;

    const isLastItem = surveyItemCodes.indexOf(itemCode) === surveyItemCodes.length - 1;

    const isLastSequence = filteredSequences.indexOf(sequence) === filteredSequences.length - 1;

    const isLastLevel = filteredTextLevels.indexOf(level) === filteredTextLevels.length - 1;

    const isLastType = availableKssTextTypes.indexOf(kssTextType) === availableKssTextTypes.length - 1;

    const allTextLevelSequenceCombinationsWereEntered = isLastItem && isLastType && isLastLevel && isLastSequence;
    const nextSequenceShouldBePreselected = (!isLastType && !isLastLevel && !isLastSequence)
      || (!isLastType && isLastLevel && !isLastSequence)
      || (isLastType && !isLastLevel && !isLastSequence)
      || (isLastType && isLastLevel && !isLastSequence);
    const nextLevelAndSequenceShouldBePreselected = (!isLastType && !isLastLevel && isLastSequence)
      || (isLastType && !isLastLevel && isLastSequence);
    const nextTypeLevelAndSequenceShouldBePreselected = (!isLastType && isLastLevel && isLastSequence);
    const nextSurveyItemShouldBePreselected = (!isLastItem && isLastType && isLastLevel && isLastSequence);

    if (allTextLevelSequenceCombinationsWereEntered) {
      return INITIAL_KSS_TEXT_VALUES;
    }

    if (nextSequenceShouldBePreselected) {
      const nextSequence = filteredSequences[filteredSequences.indexOf(sequence) + 1];

      return {
        itemCode,
        kssTextType,
        level,
        sequence: nextSequence,
      };
    }

    if (nextLevelAndSequenceShouldBePreselected) {
      const nextLevel = filteredTextLevels[filteredTextLevels.indexOf(level) + 1];
      const nextSequence = availableSequences.find(
        kssTextSequence => kssTextSequence.kssTextType === kssTextType && kssTextSequence.level === nextLevel,
      ).sequences[0];

      return {
        itemCode,
        kssTextType,
        level: nextLevel,
        sequence: nextSequence,
      };
    }

    if (nextTypeLevelAndSequenceShouldBePreselected) {
      const nextType = availableKssTextTypes[availableKssTextTypes.indexOf(kssTextType) + 1];
      const nextLevel = availableTextLevels.find(textLevel => textLevel.kssTextType === nextType).levels[0];
      const nextSequence = availableSequences.find(
        kssTextSequence => kssTextSequence.kssTextType === nextType && kssTextSequence.level === nextLevel,
      ).sequences[0];

      return {
        itemCode,
        kssTextType: nextType,
        level: nextLevel,
        sequence: nextSequence,
      };
    }

    if (nextSurveyItemShouldBePreselected) {
      const nextItem = surveyItemCodes[surveyItemCodes.indexOf(itemCode) + 1];
      const nextType = availableKssTextTypes[0];
      const nextLevel = availableTextLevels.find(textLevel => textLevel.kssTextType === nextType).levels[0];
      const nextSequence = availableSequences.find(
        kssTextSequence => kssTextSequence.kssTextType === nextType && kssTextSequence.level === nextLevel,
      ).sequences[0];

      return {
        itemCode: nextItem,
        kssTextType: nextType,
        level: nextLevel,
        sequence: nextSequence,
      };
    }

    return INITIAL_KSS_TEXT_VALUES;
  };

  return {
    getLibraryInput,
    getCompetencyInput,
    getAbilityInput,
    getAbilityForExistingCompetencyInput,
    getItemsInput,
    getKssTextsInput,
    getHeader,
    getNextKssTextValues,
  };
};

export default CommonService;
